import type {
  DensitySnapshot,
  DensityStateView,
  InstrumentRef,
  InstrumentStatusView,
  LockedCard,
  LostCauseView,
  MarketActivitySnapshot,
  MarketKind,
  ServerMessage,
  SideView,
  SituationView,
  WrappedCoin,
} from "@screener/protocol";
import { instrumentKey } from "./cards";

/**
 * Состояние клиента, набранное из потока §7.6. Живые плотности лежат словарём
 * по `density_id`: идентичность плотности задана именем, и строка обязана
 * обновляться по нему, а не по месту в списке.
 */
export interface StreamState {
  /**
   * Непрозрачная метка запуска демона. Её смена между приветствиями значит
   * перезапуск демона: состояние сбрасывается целиком (§7.6).
   */
  run: string | null;
  densities: Record<string, DensitySnapshot>;
  instruments: InstrumentStatusView[];
  situations: SituationView[];
  outcomes: OutcomeEntry[];
  /**
   * Перечень обёрнутых монет из приветствия (§3.2 1.27). Новое приветствие
   * заменяет его целиком, в том числе пустым: перечень — настройка демона,
   * не накопленное состояние.
   */
  wrapped_coins: WrappedCoin[];
  /**
   * Накопительный индекс «ключ инструмента → база в написании биржи» (§63).
   * Нужен местам без снимка в руках: ситуации и срабатывания алертов. Растёт
   * записями из снимков, не теряет их с уходом плотности и сбрасывается
   * сменой запуска демона.
   */
  exchange_bases: ReadonlyMap<string, string>;
  /**
   * Рынки, которые фронт сообщил недоступными (§132): ключ `marketKey`, значение —
   * момент потери последнего `Live`, UTC, мс. Приветствие задаёт карту целиком,
   * `market_availability` добавляет или снимает рынок. Клиент сам рынок
   * недоступным не объявляет.
   */
  marketsDown: ReadonlyMap<string, number>;
  /**
   * Последний снимок активности рынка (§145 п. 8): приветствие задаёт его,
   * сообщение `market_activity` заменяет целиком. Демон без поля — снимка нет.
   * Клиент множитель, уровень и сессию не считает.
   */
  marketActivity: MarketActivitySnapshot | null;
  /**
   * Закрытые карточки тарифа (протокол 12): по ключу сессии. Приветствие
   * заполняет карту заново, `locked_card` кладёт, заменяет или убирает запись.
   * Это не плотности: в таблицу, вид по монете, счётчики и ситуации они не
   * попадают — только в сетку карточек.
   */
  locked: ReadonlyMap<string, LockedCard>;
  /** Порядковый номер последнего применённого сообщения — для диагностики. */
  lastSeq: number | null;
}

/** Исход, который §9 пускает в ленту недавних: рынок, а не мы. */
export type RibbonOutcome = Extract<DensityStateView, "eaten" | "pulled" | "lost">;

/**
 * Запись ленты недавних исходов §9. Собирается из последнего известного
 * снимка плотности: терминальное событие снимка не несёт вовсе (§7.6).
 */
export interface OutcomeEntry {
  density_id: string;
  instrument: InstrumentRef;
  side: SideView;
  anchor_price: string;
  density_usd: string;
  /**
   * База в написании биржи из последнего снимка (§63): ею лента пишет
   * инструмент тикером. Пустая — писатель прежней версии её не прислал.
   */
  exchange_base: string;
  outcome: RibbonOutcome;
  /** Причина потери: только у Lost, и не у каждого — старый демон её не шлёт. */
  lost_cause?: LostCauseView;
  ts_ms: number;
}

/** Глубина ленты недавних исходов (§9 1.9). */
export const OUTCOME_RIBBON_DEPTH = 20;

/** Сколько записей ленты видно постоянно, без раскрытия (§9 1.9). */
export const OUTCOME_RIBBON_VISIBLE = 3;

/**
 * Терминальность состояния §4.4. Записана полной картой по `DensityStateView`,
 * а не списком: список остался бы валидным при расширении перечня состояний,
 * и новое терминальное состояние дало бы строку, которая никогда не уйдёт
 * из таблицы. Карта ломает сборку — а §9 прямо запрещает строку,
 * дотлевающую на месте живой.
 */
const TERMINAL_STATES: Record<DensityStateView, boolean> = {
  detected: false,
  pending: false,
  confirmed: false,
  approached: false,
  tested: false,
  held: false,
  eaten: true,
  pulled: true,
  vanished: true,
  lost: true,
  stopped: true,
};

/** Стадии, которые вообще могут стоять в таблице: терминальные уходят из неё. */
export const LIVE_STATES: DensityStateView[] = (
  Object.keys(TERMINAL_STATES) as DensityStateView[]
).filter((state) => !TERMINAL_STATES[state]);

/**
 * Стадии, предлагаемые к отбору. Терминальные §4.4 в таблице не стоят вовсе,
 * а `Detected` по §4.4 уходит в `Pending` немедленно. Строка в нём бывает один
 * кадр между двумя сообщениями рождения, и на экране она приравнена к `Pending`
 * (`isUnconfirmed`), так что отдельный фильтр по ней не нужен.
 */
export const FILTERABLE_STATES: DensityStateView[] = LIVE_STATES.filter(
  (state) => state !== "detected",
);

const RIBBON_OUTCOMES: readonly RibbonOutcome[] = ["eaten", "pulled", "lost"];

export const initialStreamState: StreamState = {
  run: null,
  densities: {},
  instruments: [],
  situations: [],
  outcomes: [],
  wrapped_coins: [],
  exchange_bases: new Map(),
  marketsDown: new Map(),
  marketActivity: null,
  locked: new Map(),
  lastSeq: null,
};

/** Ключ рынка биржи в `StreamState.marketsDown`: «binance:perp». */
export function marketKey(exchange: string, market: MarketKind): string {
  return `${exchange}:${market}`;
}

/**
 * Сообщение о доступности рынка (протокол 10). Номера у него нет, `lastSeq`
 * не двигается. Число — рынок добавлен или момент обновлён, `null` — снят.
 * Повтор того же состояния возвращает прежний объект.
 */
function applyMarketAvailability(
  state: StreamState,
  message: ServerMessage & { type: "market_availability" },
): StreamState {
  const key = marketKey(message.exchange, message.market);
  const since = message.down_since_ms;
  if (since === null) {
    if (!state.marketsDown.has(key)) {
      return state;
    }
    const marketsDown = new Map(state.marketsDown);
    marketsDown.delete(key);
    return { ...state, marketsDown };
  }
  if (state.marketsDown.get(key) === since) {
    return state;
  }
  const marketsDown = new Map(state.marketsDown);
  marketsDown.set(key, since);
  return { ...state, marketsDown };
}

/**
 * Дописывает в индекс баз биржи записи из снимков (§63). Индекс накопительный
 * и без удаления: ситуация или срабатывание алерта могут прийти уже после
 * ухода плотности. Пустая база в индекс не попадает — вызывающий
 * откатывается на приведённую (`tickerLabel`). Ссылка на `Map` меняется
 * только при новой или изменившейся записи, иначе возвращается прежняя.
 */
function withExchangeBases(
  index: ReadonlyMap<string, string>,
  snapshots: Iterable<DensitySnapshot>,
): ReadonlyMap<string, string> {
  let next: Map<string, string> | null = null;
  for (const density of snapshots) {
    if (density.exchange_base === "") {
      continue;
    }
    const key = instrumentKey(density.instrument);
    if ((next ?? index).get(key) !== density.exchange_base) {
      next ??= new Map(index);
      next.set(key, density.exchange_base);
    }
  }
  return next ?? index;
}

/**
 * Дописывает в индекс базы из статусов инструментов (ТЗ 1.52, §65 п. 4):
 * инструмент без плотностей — закреплённая пустая карточка — иначе остался
 * бы без написания биржи. Отсутствующее, `null` и пустое поле индекс не
 * трогает: ни не стирает, ни не пишет.
 */
function withStatusExchangeBases(
  index: ReadonlyMap<string, string>,
  statuses: Iterable<{ instrument: InstrumentRef; exchange_base?: string | null }>,
): ReadonlyMap<string, string> {
  let next: Map<string, string> | null = null;
  for (const status of statuses) {
    const base = status.exchange_base;
    if (base === undefined || base === null || base === "") {
      continue;
    }
    const key = instrumentKey(status.instrument);
    if ((next ?? index).get(key) !== base) {
      next ??= new Map(index);
      next.set(key, base);
    }
  }
  return next ?? index;
}

function isTerminal(state: DensityStateView): boolean {
  return TERMINAL_STATES[state];
}

function isRibbonOutcome(state: DensityStateView): state is RibbonOutcome {
  return (RIBBON_OUTCOMES as readonly DensityStateView[]).includes(state);
}

function byDensityId(densities: DensitySnapshot[]): Record<string, DensitySnapshot> {
  const result: Record<string, DensitySnapshot> = {};
  for (const density of densities) {
    result[density.density_id] = density;
  }
  return result;
}

type Densities = Record<string, DensitySnapshot>;

/**
 * Владелец словаря `densities` внутри пачки: тот объект, который создан в этой
 * пачке и потому может меняться на месте. Пришедший снаружи словарь владельцем
 * не помечен и не мутируется никогда — первая запись копирует его один раз.
 * Одиночный `reduceStreamState` берёт свежего владельца без словаря, поэтому
 * сохраняет прежнюю иммутабельную семантику.
 */
interface DensitiesOwner {
  current: Densities | null;
}

/** Словарь, в который можно писать: свой — как есть, чужой — одна копия на пачку. */
function writableDensities(state: StreamState, owner: DensitiesOwner): Densities {
  if (owner.current !== null && owner.current === state.densities) {
    return owner.current;
  }
  owner.current = { ...state.densities };
  return owner.current;
}

function applyHello(
  state: StreamState,
  message: ServerMessage & { type: "hello" },
  owner: DensitiesOwner,
): StreamState {
  // Приветствие обнуляет ленту всегда (§9): терминальные события за время
  // разрыва не доезжают и снимком не восполняются — снимок несёт только живые
  // плотности, а у терминала снимка нет вовсе. Лента, пережившая разрыв, была
  // бы лентой с молчаливыми дырами, и по ней нельзя судить, что происходило.
  // Довод в пользу сохранения накопленного записан открытым вопросом:
  // он меняет ТЗ, а не код.
  const densities = byDensityId(message.densities);
  owner.current = densities;
  return {
    run: message.run,
    densities,
    instruments: message.instruments,
    situations: message.situations,
    outcomes: [],
    // Демон до ревизии 1.27 поля не шлёт — перечень пуст, пометок нет.
    wrapped_coins: message.wrapped_coins ?? [],
    // Тот же запуск — индекс переживает переподключение; новый запуск — с нуля.
    // Сперва статусы, потом плотности: снимок плотности выигрывает.
    exchange_bases: withExchangeBases(
      withStatusExchangeBases(
        message.run === state.run ? state.exchange_bases : new Map(),
        message.instruments,
      ),
      message.densities,
    ),
    // Демон до протокола 10 перечня не шлёт — недоступных рынков нет.
    marketsDown: new Map(
      (message.markets_down ?? []).map((entry) => [
        marketKey(entry.exchange, entry.market),
        entry.down_since_ms,
      ]),
    ),
    // Демон до протокола 11 снимка не шлёт — пометки нет.
    marketActivity: message.market_activity ?? null,
    // Демон до протокола 12 и сессия без ограничений закрытых карточек не шлют.
    locked: new Map((message.locked ?? []).map((card) => [card.key, card])),
    lastSeq: message.seq,
  };
}

/**
 * Имя ситуации §126: `density_id` её первого участника. Пустой состав имени
 * не даёт — такой ситуации в изменении быть не должно.
 */
function situationName(situation: SituationView): string | null {
  return situation.members[0]?.density_id ?? null;
}

/**
 * Изменение состава ситуаций (§7.6, протокол 9). Сперва `removed`, потом
 * `upserted`: смена первого участника приходит как «старое имя ушло, новая
 * ситуация появилась». Индекс «имя → место» собирается на каждом изменении,
 * отдельной карты в состоянии нет. Неизвестное имя в `removed` — расхождение
 * состава с демоном: предупреждение и пропуск. Неизвестное имя в `upserted` —
 * штатное добавление в конец.
 */
function applySituationsDelta(
  state: StreamState,
  message: ServerMessage & { type: "situations_delta" },
): StreamState {
  let situations = state.situations;
  let changed = false;

  if (message.removed.length > 0) {
    const known = new Set<string>();
    for (const situation of situations) {
      const name = situationName(situation);
      if (name !== null) {
        known.add(name);
      }
    }
    const doomed = new Set<string>();
    for (const name of message.removed) {
      if (known.has(name)) {
        // Повтор известного имени в одном изменении безвреден.
        doomed.add(name);
      } else {
        console.warn(`situations_delta: удаление неизвестной ситуации «${name}»`);
      }
    }
    if (doomed.size > 0) {
      situations = situations.filter((situation) => {
        const name = situationName(situation);
        return name === null || !doomed.has(name);
      });
      changed = true;
    }
  }

  if (message.upserted.length > 0) {
    const index = new Map<string, number>();
    situations.forEach((situation, place) => {
      const name = situationName(situation);
      if (name !== null) {
        index.set(name, place);
      }
    });
    const next = [...situations];
    for (const situation of message.upserted) {
      const name = situationName(situation);
      if (name === null) {
        console.warn("situations_delta: ситуация без участников в upserted пропущена");
        continue;
      }
      const place = index.get(name);
      if (place === undefined) {
        index.set(name, next.length);
        next.push(situation);
      } else {
        next[place] = situation;
      }
      changed = true;
    }
    if (changed) {
      situations = next;
    }
  }

  if (!changed) {
    return { ...state, lastSeq: message.seq };
  }
  return { ...state, situations, lastSeq: message.seq };
}

function applyEvent(
  state: StreamState,
  message: ServerMessage & { type: "event" },
  owner: DensitiesOwner,
): StreamState {
  const known = state.densities[message.density_id];
  const snapshot = message.density ?? known;
  const exchange_bases =
    message.density === null || message.density === undefined
      ? state.exchange_bases
      : withExchangeBases(state.exchange_bases, [message.density]);

  if (message.body.kind === "transition" && isTerminal(message.body.to)) {
    const outcome = message.body.to;
    const lost_cause = message.body.lost_cause;
    const outcomes =
      isRibbonOutcome(outcome) && snapshot !== undefined
        ? [
            {
              density_id: message.density_id,
              instrument: snapshot.instrument,
              side: snapshot.side,
              anchor_price: snapshot.anchor_price,
              density_usd: snapshot.density_usd,
              exchange_base: snapshot.exchange_base,
              outcome,
              ...(outcome === "lost" && lost_cause !== undefined ? { lost_cause } : {}),
              ts_ms: message.ts_ms,
            },
            ...state.outcomes,
          ].slice(0, OUTCOME_RIBBON_DEPTH)
        : state.outcomes;

    let densities = state.densities;
    if (densities[message.density_id] !== undefined) {
      densities = writableDensities(state, owner);
      delete densities[message.density_id];
    }

    return {
      ...state,
      densities,
      outcomes,
      exchange_bases,
      lastSeq: message.seq,
    };
  }

  if (snapshot === undefined) {
    // Нетерминальное событие без снимка по неизвестной плотности: показывать
    // нечего, и придумывать строку клиент не вправе.
    return { ...state, lastSeq: message.seq };
  }

  const densities = writableDensities(state, owner);
  densities[message.density_id] = snapshot;

  return {
    ...state,
    densities,
    exchange_bases,
    lastSeq: message.seq,
  };
}

/**
 * Срез живых величин §7.6 (1.13): пять посканных величин заменяются у тех
 * строк, которые клиент уже знает.
 *
 * Срез — не событие: номера у него нет, и `lastSeq` он не двигает. Запись
 * о неизвестной плотности отбрасывается: пяти величин не хватит на строку,
 * а строку из ничего клиент не сочиняет — недостающие плотности приходят
 * приветствием и событиями. Плотность, которой в срезе нет, не изменилась
 * и остаётся на месте.
 *
 * Без единого совпадения возвращается прежний объект состояния: React
 * перерисовывает по ссылке, и новый объект на каждом срезе перерисовывал бы
 * таблицу впустую.
 */
function sameLiveValues(
  known: DensitySnapshot,
  live: Extract<ServerMessage, { type: "live_values" }>["values"][number],
): boolean {
  return (
    known.distance_pct === live.distance_pct &&
    known.density_usd === live.density_usd &&
    known.density_qty === live.density_qty &&
    known.current_levels_count === live.current_levels_count &&
    known.mm_flags.length === live.mm_flags.length &&
    known.mm_flags.every((flag, index) => flag === live.mm_flags[index])
  );
}

function applyLiveValues(
  state: StreamState,
  message: ServerMessage & { type: "live_values" },
  owner: DensitiesOwner,
): StreamState {
  let densities: Densities | null = null;

  for (const live of message.values) {
    const known = (densities ?? state.densities)[live.density_id];
    if (known === undefined || sameLiveValues(known, live)) {
      // Те же величины — прежний снимок: строки и карточки, чьи ссылки не
      // менялись, не перерисовываются (мемоизация вида «карточки»).
      continue;
    }

    densities ??= writableDensities(state, owner);
    densities[live.density_id] = {
      ...known,
      distance_pct: live.distance_pct,
      density_usd: live.density_usd,
      density_qty: live.density_qty,
      current_levels_count: live.current_levels_count,
      mm_flags: live.mm_flags,
    };
  }

  return densities === null ? state : { ...state, densities };
}

/**
 * Изменение закрытой карточки (протокол 12). Номер есть, как у `event`:
 * `lastSeq` двигается. Неизвестный ключ в `removed` — карту не меняет.
 */
function applyLockedCard(
  state: StreamState,
  message: ServerMessage & { type: "locked_card" },
): StreamState {
  const { change } = message;
  if (change.op === "removed") {
    if (!state.locked.has(change.key)) {
      return { ...state, lastSeq: message.seq };
    }
    const locked = new Map(state.locked);
    locked.delete(change.key);
    return { ...state, locked, lastSeq: message.seq };
  }
  const { op: _op, ...card } = change;
  const locked = new Map(state.locked);
  locked.set(card.key, card);
  return { ...state, locked, lastSeq: message.seq };
}

function sameInstrument(left: InstrumentRef, right: InstrumentRef): boolean {
  return (
    left.exchange === right.exchange &&
    left.market === right.market &&
    left.base === right.base &&
    left.quote === right.quote
  );
}

function applyInstrumentStatus(
  state: StreamState,
  message: ServerMessage & { type: "instrument_status" },
): StreamState {
  const known = state.instruments.some((entry) =>
    sameInstrument(entry.instrument, message.instrument),
  );
  const instruments = known
    ? state.instruments.map((entry) =>
        sameInstrument(entry.instrument, message.instrument)
          ? { instrument: entry.instrument, status: message.status }
          : entry,
      )
    : [...state.instruments, { instrument: message.instrument, status: message.status }];

  const exchange_bases = withStatusExchangeBases(state.exchange_bases, [message]);

  return { ...state, instruments, exchange_bases, lastSeq: message.seq };
}

/**
 * Один переход состояния клиента на одно сообщение потока. Неделимость здесь
 * не стилистическая: терминальный переход §4.4 одновременно убирает строку
 * и добавляет запись в ленту, и промежуточного состояния между этими двумя
 * действиями быть не должно.
 */
export function reduceStreamState(state: StreamState, message: ServerMessage): StreamState {
  return reduceWithOwner(state, message, { current: null });
}

function reduceWithOwner(
  state: StreamState,
  message: ServerMessage,
  owner: DensitiesOwner,
): StreamState {
  switch (message.type) {
    case "hello":
      return applyHello(state, message, owner);
    case "event":
      return applyEvent(state, message, owner);
    case "instrument_status":
      return applyInstrumentStatus(state, message);
    case "situations":
      return { ...state, situations: message.situations, lastSeq: message.seq };
    case "situations_delta":
      return applySituationsDelta(state, message);
    case "live_values":
      return applyLiveValues(state, message, owner);
    case "market_availability":
      return applyMarketAvailability(state, message);
    case "locked_card":
      return applyLockedCard(state, message);
    case "market_activity": {
      // Сообщение — снимок целиком плюс тег; номера у него нет, `lastSeq` не двигается.
      const { type: _type, ...marketActivity } = message;
      return { ...state, marketActivity };
    }
    case "alert_fired":
      // Срабатывание — событие прошлого, а не состояние экрана (§7.6 1.20):
      // строк оно не трогает, но номер занимает. Звук и нотификация —
      // change `stage6-alert-web`.
      return { ...state, lastSeq: message.seq };
    default:
      // Вид сообщения, которого клиент не знает. `ipc-contract` называет его
      // появление ломающим изменением — но демон раздаёт статику сам, и старая
      // вкладка рядом с новым демоном возможна. Неизвестное сообщение обязано
      // ничего не менять, а не гасить экран трейдера.
      return state;
  }
}

/**
 * Применение пачки сообщений целиком, в порядке поступления. Буфер копится
 * между кадрами анимации и применяется одним переходом: перерисовка на каждое
 * событие дала бы сотни перерисовок в секунду и ни одного нового знания
 * на экране.
 */
export function applyMessages(state: StreamState, messages: ServerMessage[]): StreamState {
  // Словарь densities копируется не больше одного раза на пачку: копия, созданная
  // внутри пачки, дальше меняется на месте. Входное состояние не мутируется.
  const owner: DensitiesOwner = { current: null };
  let next = state;
  for (const message of messages) {
    next = reduceWithOwner(next, message, owner);
  }
  return next;
}
